import {
  Organisation,
  OrganisationsForSageLinking,
  OrganisationWrapper,
  HoldingOrgSetupRequest
} from '@futrli-sdk/organisations';
import { Integration } from '@futrli-sdk/app-store';
import { Workspace, WorkspacesListingItem } from '@futrli-sdk/workspaces';
import {
  API_PATHS,
  getAxiosResponseData,
  interpolatePathParams,
  REQUEST_METHODS,
  ROUTE_PARAMS
} from '@futrli/shared-services';
import { APIClients } from '@futrli/shared-services';

/**
 * Fetches a list of organisations for a workspace.
 *
 * @param {string} workspaceId
 * @returns {Promise<Organisation[]>}
 */
export const fetchOrganisations = async (workspaceId: string): Promise<Organisation[]> => {
  const path = interpolatePathParams(API_PATHS.ORGANISATIONS, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return getAxiosResponseData(response.data);
};

/**
 * Fetches a list of workspaces for user.
 *
 * @param {string} workspaceId
 * @returns {Promise<Workspace[]>}
 */
export const fetchWorkspaces = async (): Promise<Workspace[]> => {
  const path = API_PATHS.WORKSPACES;
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return getAxiosResponseData(response.data);
};

/**
 * Updates workspace.
 *
 * @param {string} workspaceId
 * @returns {Promise<WorkspacesListingItem>}
 */
export const updateWorkspace = async (
  workspaceId: string,
  data: Partial<{ name: string | null; is_default_workspace: boolean }>
): Promise<WorkspacesListingItem> => {
  const path = interpolatePathParams(API_PATHS.WORKSPACE_BY_ID, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path,
        method: REQUEST_METHODS.PATCH,
        data
      }
    }
  });
  return getAxiosResponseData(response.data);
};

/**
 * Fetches all workspaces data for user.
 *
 * @returns {Promise<WorkspacesListingItem[]>}
 */
export const fetchWorkspacesData = async (): Promise<WorkspacesListingItem[]> => {
  const path = API_PATHS.ALL_WORKSPACES_DATA;
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return getAxiosResponseData(response.data);
};

/**
 * Fetches integrations.
 *
 * @returns {Promise<Integration[]>}
 */
export const fetchIntegrations = async (): Promise<Integration[]> => {
  const path = API_PATHS.INTEGRATIONS;
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return getAxiosResponseData(response.data);
};

/**
 * List orgs available for linking (SFA onboarding flow)
 * @param workspaceId - ID of the workspace the holidng org is in
 * @param organisationId - ID of the holding org
 */
export const fetchOrgsAvailableForLinking = async (workspaceId: string, organisationId: string) => {
  const path = interpolatePathParams(API_PATHS.ORGANISATIONS_FOR_LINKING, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        url: path
      }
    }
  });
  return getAxiosResponseData<OrganisationsForSageLinking>(response);
};

/**
 * Set up a holding org as linked with another org (merged)
 * @param workspaceId - ID of the workspace the holidng org is in
 * @param organisationId - ID of the holding org
 * @param linkedOrgId - ID of the org to link the holding org with
 */
export const setupHoldingOrgLink = async (workspaceId: string, organisationId: string, linkedOrgId: string) => {
  const path = interpolatePathParams(API_PATHS.ORGANISATIONS_FOR_LINKING, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });
  const data: Pick<HoldingOrgSetupRequest, 'link'> = {
    link: {
      link_with_organisation_id: linkedOrgId
    }
  };
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: 'POST',
        url: path,
        data
      }
    }
  });
  return getAxiosResponseData<OrganisationWrapper>(response).data;
};

/**
 * Adds a datasource to a holding org
 * @param workspaceId - ID of the workspace the holidng org is in
 * @param organisationId - ID of the holding org
 * @param integrationId - ID of the datasource / integration to attach to the holding org
 */
export const setupHoldingOrgWithNewDatasource = async (
  workspaceId: string,
  organisationId: string,
  integrationId: string
) => {
  const path = interpolatePathParams(API_PATHS.ORGANISATIONS_FOR_LINKING, {
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId,
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId
  });
  const data: Pick<HoldingOrgSetupRequest, 'add_datasource'> = {
    add_datasource: {
      integration_id: integrationId
    }
  };
  const response = await APIClients.getAPIClient().makeRequest({
    payload: {
      request: {
        method: 'POST',
        url: path,
        data
      }
    }
  });
  return getAxiosResponseData<OrganisationWrapper>(response).data;
};
